import React from 'react';
import { BadgeCheckIcon } from 'lucide-react';
import { demoIdentity } from '../../data/landingVisual';
import { cn } from '../../utils/cn';

/**
 * The Campuzon digital credential.
 *
 * Built to read as a credential rather than a profile card — portrait, the
 * fields an institution actually confirms, and a machine strip along the
 * bottom. It carries a demonstration label because the person on it is not a
 * real student, and "Verified student" is a claim we only ever make where an
 * institution has actually confirmed it.
 */
interface IdentityCredentialProps {
  className?: string;
  /** Compact drops the machine strip for tight compositions. */
  compact?: boolean;
}
export function IdentityCredential({
  className,
  compact = false
}: IdentityCredentialProps) {
  return <figure className={cn('overflow-hidden rounded-2xl border border-white/25 bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.35)]', className)} data-id="element-2615" data-mp="5osxbgm3tpeb">
      <div className="flex items-start gap-3 p-3.5" data-id="element-2616" data-mp="al9nshgs0fuk">
        <img src={demoIdentity.photo} alt="" loading="lazy" decoding="async" className="h-16 w-14 shrink-0 rounded-lg object-cover" data-id="element-2617" data-mp="1izopptolenh0" />
        <div className="min-w-0 flex-1" data-id="element-2618" data-mp="1sxumxxzgf5fj">
          <p className="truncate text-body-sm font-bold uppercase tracking-wide text-ink" data-id="element-2619" data-mp="1suqcwjzb5fgy">
            {demoIdentity.name}
          </p>
          <p className="mt-1 truncate text-caption text-ink-muted" data-id="element-2620" data-mp="21wzkb0za4sq4">
            {demoIdentity.institution} · {demoIdentity.campus}
          </p>
          <p className="mt-0.5 truncate text-caption text-ink" data-id="element-2621" data-mp="1xveajoi1kuk5">{demoIdentity.programme}</p>
          <p className="mt-0.5 text-caption text-ink-muted" data-id="element-2622" data-mp="2bmswa33c39uw">{demoIdentity.level}</p>
        </div>
      </div>

      {!compact && <div aria-hidden className="flex h-6 items-end gap-[2px] overflow-hidden px-3.5" data-id="element-2623" data-mp="26qkc2baa4qgv">
          {Array.from({
        length: 54
      }).map((_, index) => <span key={index} className="block bg-ink/80" style={{
        width: index % 3 === 0 ? 3 : 1,
        height: index % 5 === 0 ? 16 : 12
      }} data-id="element-2624" data-mp="2gngkyi43bely" data-mpi={index} />)}
        </div>}

      <figcaption className="mt-2.5 flex items-center justify-between gap-2 border-t border-line px-3.5 py-2.5" data-id="element-2625" data-mp="2soyf5n53uff6">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-caption font-semibold text-brand-800" data-id="element-2626" data-mp="2xloug89356zj">
          <BadgeCheckIcon aria-hidden className="h-3.5 w-3.5" data-id="element-2627" data-mp="37e4edb3y05n2" />
          Verified student
        </span>
        <span className="text-caption text-ink-subtle" data-id="element-2628" data-mp="3beeqz6m8puaw">Demonstration</span>
      </figcaption>
    </figure>;
}